import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, DollarSign, Loader2 } from 'lucide-react';
import {
  loadStripe,
  type Stripe,
  type StripeElementsOptions,
  type StripeExpressCheckoutElementConfirmEvent,
  type StripeExpressCheckoutElementReadyEvent,
  type StripeExpressCheckoutElementClickEvent,
} from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useToast } from '@/contexts/ToastContext';
import { formatCurrency } from '@/utils/formatters';
import type {
  CreditOption,
  CreditApplicationInput,
} from '@/api/extensions/useRegistrationOps';
import { CreditApplyControl } from './CreditApplyControl';
import { paymentErrorCopy } from './paymentErrorCopy';

/**
 * Quote returned by the parent's `quoteOrder` callback. The deferred-mint
 * dialog mounts Stripe Elements in `mode: 'payment'` against this quote
 * (no client_secret yet) and only mints the actual Stripe PaymentIntent
 * at submit time via `confirmIntent` below.
 *
 * Replaces the prior eager-mint `StripeOrderLike` shape: there is no
 * `client_secret` or `stripe_payment_intent_id` at quote time — both are
 * returned by `confirmIntent` only when the user actually clicks Pay.
 */
export interface DeferredStripeQuote {
  /** Local payment row id. Frontend passes this to `confirmIntent` at submit. */
  payment_id: string;
  /** Stripe publishable key — used to load Stripe.js. */
  stripe_publishable_key?: string;
  /**
   * Totals quoted for BOTH payment methods so the dialog can show the
   * card-vs-ACH price difference client-side without a backend round-trip
   * on every toggle. ACH waives the transaction fee.
   */
  totals_by_method?: {
    card: { total_amount: number; transaction_fee_total: number };
    us_bank_account: { total_amount: number; transaction_fee_total: number };
  };
  /** Card-priced fallback when totals_by_method is absent. */
  total_amount?: number;
  transaction_fee_total?: number;
}

/** Result returned by confirmIntent at submit time. */
export interface ConfirmIntentResult {
  client_secret: string;
  stripe_payment_intent_id: string;
  total_amount: number;
  transaction_fee_total: number;
  stripe_publishable_key: string;
}

interface StripeCheckoutDialogProps<TQuote extends DeferredStripeQuote> {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  /** Label for the Amount card — e.g. "Amount due" or "Total due". */
  amountLabel: string;
  /**
   * Pre-known total to surface before the quote arrives. The quoted
   * total takes precedence once it lands.
   */
  fallbackTotal?: number | null;
  /**
   * Stable identifier — when it changes, the dialog re-fetches the
   * quote. For single-pay this is the registrationId; for bulk it's a
   * JSON-stringified id list.
   */
  triggerKey: string;
  /**
   * Fetch the deferred-mint quote. Called on open + when triggerKey
   * changes. Returns payment_id + totals; does NOT mint a Stripe PI.
   */
  quoteOrder: () => Promise<TQuote>;
  /**
   * Mint the Stripe PaymentIntent NOW for the chosen method. Called
   * when the user clicks Pay. Returns client_secret which the dialog
   * immediately hands to stripe.confirmPayment.
   */
  confirmIntent: (
    paymentId: string,
    paymentMethodType: 'card' | 'us_bank_account',
    /** Org credit to apply (split tender, single-org). The dialog injects the
     *  amount the user chose; mint reduces the Stripe charge by it. Omitted/0 =
     *  no credit. */
    creditAmount?: number,
    /** Multi-org split tender: per-org credit contributions. Passed alongside
     *  creditAmount (= their sum) when the basket spans orgs. */
    creditApplications?: CreditApplicationInput[],
  ) => Promise<ConfirmIntentResult>;
  /**
   * Org account credit available to apply toward this payment. When > 0 the
   * dialog shows an "apply credit" control; the chosen amount reduces the
   * Stripe charge and is passed to confirmIntent. Org-scoping is enforced
   * server-side. Omit / 0 to hide the control. Used by the single-org
   * (PayNow / manual) flows; bulk uses `creditOptions` instead.
   */
  availableCredit?: number;
  /**
   * Per-org credit affordances for a BULK basket (org-scoped). When provided,
   * the dialog runs in multi-org mode: a single option renders the inline
   * control (with a "total credit / available" line); multiple options render
   * a dropdown + per-org badges. When credit covers the whole basket the
   * dialog offers a no-Stripe "Pay with credit" button (see onSettleWithCredit).
   * Mutually exclusive with `availableCredit`.
   */
  creditOptions?: CreditOption[];
  /**
   * Settle the basket ENTIRELY with credit (no Stripe charge). Required for the
   * zero-charge path to appear; called with the per-org applications when the
   * applied credit covers 100% of the basket.
   */
  onSettleWithCredit?: (
    applications: CreditApplicationInput[],
  ) => Promise<void>;
  /**
   * Captures the confirmed PaymentIntent. Called after Stripe Elements
   * reports a successful confirmation.
   */
  onApprove: (
    paymentIntentId: string,
    methodInfo: { paymentMethodType: string | null; status: string },
  ) => Promise<void>;
  /**
   * Called when the user dismisses the dialog without completing
   * payment. Default is a "Payment cancelled" toast. Because deferred-mint
   * never creates a Stripe PI until submit, there is typically nothing
   * to clean up on Stripe's side — but local registration rows may still
   * need abandoning, which the parent owns.
   */
  onCancel?: () => void;
  /** Side effects after onApprove fully resolves (toasts, navigation, etc.). */
  onApproved?: () => void;
}

/**
 * Inner Elements form. Rendered inside <Elements /> — needs to be a
 * separate component so it can use useStripe() / useElements().
 *
 * Deferred-mint submit flow:
 *   1. elements.submit()    — validate the form locally
 *   2. confirmIntent()      — backend mints the PI with chosen method amount
 *   3. stripe.confirmPayment({ clientSecret })
 *                           — confirm the freshly-minted PI
 *
 * No Stripe-side record exists until step 2. Closing the dialog before
 * clicking Pay leaves zero artifacts in the Stripe dashboard.
 */
function StripeCheckoutForm({
  paymentId,
  confirmIntent,
  onApprove,
  onApproved,
  onClose,
  onMethodChange,
  totalsByMethod,
}: {
  paymentId: string;
  confirmIntent: (
    paymentId: string,
    paymentMethodType: 'card' | 'us_bank_account',
  ) => Promise<ConfirmIntentResult>;
  onApprove: (
    paymentIntentId: string,
    methodInfo: { paymentMethodType: string | null; status: string },
  ) => Promise<void>;
  onApproved?: () => void;
  onClose: () => void;
  /**
   * Bubble up the currently-selected method so the dialog can switch
   * the displayed total between card-price and ACH-price. No backend
   * call needed — both totals were quoted upfront.
   */
  onMethodChange?: (
    paymentMethodType: 'card' | 'us_bank_account' | null,
  ) => void;
  /**
   * Per-method totals from the quote. Drives elements.update({amount})
   * when the user toggles between card and ACH — Stripe deferred mode
   * requires the Elements options.amount to match the PaymentIntent's
   * amount at confirm time, so if we leave it pinned to the card total
   * and mint an ACH PI for the lower amount, Stripe captures the
   * Elements amount instead of the PI amount (the bug Andy reported:
   * ACH still charged the full card-priced total).
   */
  totalsByMethod?: DeferredStripeQuote['totals_by_method'];
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // ACH bank-account verification notice. When confirmPayment returns a
  // PaymentIntent in `requires_action` for a bank debit (microdeposits /
  // descriptor code), the payment is NOT failed and NOT yet processing — the
  // user must verify their bank to complete it. This is shown as an actionable
  // notice (with Stripe's hosted verification link when available), NOT the
  // generic "unexpected state" error.
  const [verifyInfo, setVerifyInfo] = useState<{
    message: string;
    url: string | null;
  } | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  // Whether the Express Checkout Element has at least one usable wallet
  // (Apple Pay / Google Pay) to show. Drives the "or pay another way"
  // divider and lets us collapse the empty wallet slot on desktop browsers
  // that have no wallet configured. `walletReady` gates the collapse so we
  // never hide the element BEFORE onReady fires (hiding it first suppresses
  // wallet detection).
  const [walletReady, setWalletReady] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState(false);
  // Last amount (in cents) we pushed to elements.update(), so we can skip
  // redundant updates — see the effect below.
  const lastAppliedAmountRef = useRef<number | null>(null);

  // Keep Elements options.amount in sync with the user's chosen method.
  // Without this Stripe captures the Elements-priced amount (card total)
  // even though /mint-intent created a PaymentIntent for the lower ACH
  // amount. elements.update() is non-disruptive — does not remount the
  // PaymentElement or clear user input.
  useEffect(() => {
    if (!elements || !totalsByMethod) return;
    const chosen =
      selectedMethod === 'us_bank_account' ? 'us_bank_account' : 'card';
    const dollars =
      chosen === 'us_bank_account'
        ? totalsByMethod.us_bank_account.total_amount
        : totalsByMethod.card.total_amount;
    if (typeof dollars !== 'number' || dollars <= 0) return;
    const cents = Math.round(dollars * 100);
    // elements.update() can make the PaymentElement re-emit its change event,
    // which flips selectedMethod and re-runs this effect. Bail when the amount
    // hasn't actually changed so that feedback can never sustain a loop.
    if (lastAppliedAmountRef.current === cents) return;
    lastAppliedAmountRef.current = cents;
    try {
      elements.update({ amount: cents });
    } catch {
      // Best-effort — Stripe surfaces a richer error if the amount is
      // genuinely invalid; the eventual confirmPayment call will catch it.
    }
  }, [elements, selectedMethod, totalsByMethod]);

  const handleMethodChange = useCallback(
    (event: { value: { type: string } }) => {
      const newType = event?.value?.type ?? null;
      setSelectedMethod(newType);
      if (newType === 'card' || newType === 'us_bank_account') {
        onMethodChange?.(newType);
      } else {
        onMethodChange?.(null);
      }
    },
    [onMethodChange],
  );

  // ─── Express Checkout Element (Apple Pay / Google Pay) ────────────────
  //
  // Wallets are intentionally DISABLED inside the PaymentElement below and
  // routed exclusively through this Express Checkout Element. Why: the
  // in-PaymentElement wallet flow opened the native Apple Pay sheet during
  // elements.submit(), then the page had to survive a mint + confirmPayment
  // round-trip AFTER the sheet closed. On iOS the web view is frequently
  // torn down on return from the native sheet, so confirmPayment never ran
  // and no charge happened (verified in prod: PaymentIntents minted then
  // cancelled with last_payment_error=null — confirm was never reached).
  //
  // The Express Checkout Element keeps its own wallet sheet open and showing
  // a spinner THROUGH the mint + confirmPayment calls, so the page underneath
  // is never exposed/reloaded between wallet authorization and confirmation.
  const handleWalletReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      setWalletReady(true);
      setWalletAvailable(!!event.availablePaymentMethods);
    },
    [],
  );

  const handleWalletClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      // Pin the shared Elements amount to the CARD price before the wallet
      // sheet opens. Wallets always pay the card total (the ACH fee waiver
      // only applies to a bank-account debit), and confirmPayment in deferred
      // mode requires the Elements amount to equal the minted PI's amount.
      const cardDollars = totalsByMethod?.card?.total_amount;
      if (elements && typeof cardDollars === 'number' && cardDollars > 0) {
        const cents = Math.round(cardDollars * 100);
        lastAppliedAmountRef.current = cents;
        try {
          elements.update({ amount: cents });
        } catch {
          // Best-effort — confirmPayment surfaces a clear error if it mismatches.
        }
      }
      event.resolve();
    },
    [elements, totalsByMethod],
  );

  // Surface an actionable verification notice when an ACH PaymentIntent comes
  // back `requires_action` (bank-account verification via microdeposits or a
  // descriptor code). Returns true when it handled the state so callers can
  // skip the generic error path. The entry is intentionally NOT confirmed here
  // — the backend keeps the payment pending until the bank is verified and the
  // transfer actually starts processing.
  const handleVerificationRequired = useCallback(
    (paymentIntent: { status?: string; next_action?: any } | null): boolean => {
      const nextAction = paymentIntent?.next_action;
      const isBankVerification =
        nextAction?.type === 'verify_with_microdeposits';
      if (!isBankVerification) return false;
      const url: string | null =
        nextAction?.verify_with_microdeposits?.hosted_verification_url ?? null;
      setVerifyInfo({
        message:
          'Your bank account needs to be verified before this payment can go ' +
          'through. Your bank will receive a small deposit (or a descriptor ' +
          'code) within 1–2 business days — verify it to complete your entry. ' +
          'Your spot is NOT confirmed until verification is complete.',
        url,
      });
      return true;
    },
    [],
  );

  const handleWalletConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }
      setErrorMessage(null);

      // 1. Validate the Elements. The wallet sheet stays open (spinner) from
      //    here through confirmPayment below — that is the entire point of
      //    using the Express Checkout Element on iOS.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        // Dismiss the open wallet sheet with an error state.
        event.paymentFailed({ reason: 'fail' });
        setErrorMessage(submitError.message ?? 'Payment failed');
        return;
      }

      // 2. Mint the PaymentIntent for the card price.
      let intent: ConfirmIntentResult;
      try {
        intent = await confirmIntent(paymentId, 'card');
      } catch (err) {
        event.paymentFailed({ reason: 'fail' });
        const msg = paymentErrorCopy(err);
        setErrorMessage(msg);
        showError(msg);
        return;
      }

      // 3. Confirm. redirect:'if_required' lets Apple/Google Pay settle in
      //    the sheet without a full-page redirect.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: intent.client_secret,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/payments/success`,
        },
      });

      if (error) {
        // Stripe closes the wallet sheet on a confirm error (declined card,
        // failed authentication). The dialog stays open and the user is about
        // to retry, so we must NOT abandon the payment: cancelling it here
        // cancels the local row too — via the payment_intent.canceled webhook
        // — and `cancelled` is terminal, so every later Pay tap died with
        // "Cannot mint intent". The next mint supersedes this intent anyway.
        setErrorMessage(error.message ?? 'Payment failed');
        showError(error.message ?? 'Payment failed');
        return;
      }

      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'processing'
      ) {
        await onApprove(paymentIntent.id, {
          paymentMethodType: 'card',
          status: paymentIntent.status,
        });
        onApproved?.();
        onClose();
      } else if (!handleVerificationRequired(paymentIntent)) {
        setErrorMessage(
          `Payment in unexpected state: ${paymentIntent?.status ?? 'unknown'}`,
        );
      }
    },
    [
      stripe,
      elements,
      paymentId,
      confirmIntent,
      onApprove,
      onApproved,
      onClose,
      showError,
      handleVerificationRequired,
    ],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      setSubmitting(true);
      setErrorMessage(null);
      setVerifyInfo(null);

      // Determine the chosen method up front. Default to card if Stripe
      // didn't fire onChange (e.g. only one method available).
      const chosen: 'card' | 'us_bank_account' =
        selectedMethod === 'us_bank_account' ? 'us_bank_account' : 'card';

      // Mint the PaymentIntent IN PARALLEL with elements.submit(). This is the
      // Apple Pay fix: for wallets, elements.submit() opens the native Apple
      // Pay / Google Pay sheet, and Stripe requires confirmPayment to run
      // promptly once it closes. The old order awaited a /mint-intent network
      // round-trip *between* the sheet and confirmPayment — on iOS the page is
      // torn down on return from the native sheet during that gap, so the
      // intent was never minted and no charge happened (verified: zero PIs in
      // Stripe for affected users). Kicking the mint off alongside the sheet
      // means the client_secret is already in hand the instant the sheet
      // closes, so confirmPayment follows immediately with no awaited gap.
      //
      // The mint still fires ONLY on this Pay click — opening the dialog and
      // closing it never mints a PI. If the user backs out of the sheet/form
      // (submit error below), we cancel the speculatively-minted PI so it
      // can't linger as an orphan in Stripe.
      const mintPromise: Promise<
        ConfirmIntentResult | { __mintError: unknown }
      > = confirmIntent(paymentId, chosen).catch((err) => ({
        __mintError: err,
      }));

      try {
        // 1. Validate the form / collect the wallet credential. For Apple Pay
        //    and Google Pay this is where the native payment sheet opens.
        const { error: submitError } = await elements.submit();
        if (submitError) {
          // The form is incomplete (mistyped card, missing CVC, bad routing
          // number). The dialog stays open and Pay is re-enabled below, so the
          // user is about to correct it and try again. Abandoning the
          // speculatively-minted intent here is what bricked that retry: it
          // cancels the local payment row via the payment_intent.canceled
          // webhook, and `cancelled` is terminal. Leave the intent alone — the
          // next mint supersedes and cancels it. If the user simply walks away
          // it lingers unconfirmed (holding no funds and no authorization);
          // whether Stripe reaps those on its own is UNVERIFIED, so a sweep may
          // be needed if they accumulate.
          setErrorMessage(submitError.message ?? 'Payment form is incomplete');
          return;
        }

        // 2. Await the in-flight mint (almost always already resolved by the
        //    time the user finishes the wallet sheet). Backend computed the
        //    right amount (ACH waives the fee) and returned client_secret.
        const minted = await mintPromise;
        if ('__mintError' in minted) {
          const msg = paymentErrorCopy(minted.__mintError);
          setErrorMessage(msg);
          showError(msg);
          return;
        }
        const intent: ConfirmIntentResult = minted;

        // 3. Confirm the freshly-minted PI.
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: intent.client_secret,
          // Stay on-page when possible; Stripe forces a redirect only for
          // a few async methods (e.g. some bank debits).
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/payments/success`,
          },
        });

        if (error) {
          setErrorMessage(error.message ?? 'Payment failed');
          showError(error.message ?? 'Payment failed');
          return;
        }

        const usedMethodType =
          (paymentIntent?.payment_method_types?.length === 1
            ? paymentIntent.payment_method_types[0]
            : null) ?? chosen;

        if (
          paymentIntent?.status === 'succeeded' ||
          paymentIntent?.status === 'processing'
        ) {
          await onApprove(paymentIntent.id, {
            paymentMethodType: usedMethodType,
            status: paymentIntent.status,
          });
          onApproved?.();
          onClose();
        } else if (!handleVerificationRequired(paymentIntent)) {
          setErrorMessage(
            `Payment in unexpected state: ${paymentIntent?.status ?? 'unknown'}`,
          );
        }
      } catch (err) {
        const msg = paymentErrorCopy(err, 'Payment confirmation failed');
        setErrorMessage(msg);
        showError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [
      stripe,
      elements,
      paymentId,
      selectedMethod,
      confirmIntent,
      onApprove,
      onApproved,
      onClose,
      showError,
      handleVerificationRequired,
    ],
  );

  return (
    <div className="space-y-3">
      {/* Apple Pay / Google Pay — the ONLY wallet entry point. Renders nothing
          on devices/browsers with no wallet available. Stays visible until
          onReady reports zero wallets, then collapses. */}
      <div className={walletReady && !walletAvailable ? 'hidden' : ''}>
        <ExpressCheckoutElement
          options={{
            // Restrict to the wallets that the in-PaymentElement flow broke on
            // iOS. Link / PayPal / etc. are intentionally not enabled here.
            paymentMethods: {
              applePay: 'auto',
              googlePay: 'auto',
              link: 'never',
              paypal: 'never',
              amazonPay: 'never',
              klarna: 'never',
            },
            buttonType: { applePay: 'plain', googlePay: 'plain' },
            layout: { maxColumns: 1, maxRows: 2 },
          }}
          onReady={handleWalletReady}
          onClick={handleWalletClick}
          onConfirm={handleWalletConfirm}
        />
      </div>
      {walletAvailable && (
        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">
            Or pay with card or bank
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card', 'us_bank_account'],
            // Wallets are routed exclusively through the Express Checkout
            // Element above — never the embedded PaymentElement wallet button
            // (that path is the iOS-teardown bug).
            wallets: { applePay: 'never', googlePay: 'never' },
          }}
          onChange={handleMethodChange}
        />
        {errorMessage && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2 text-sm flex gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}
        {verifyInfo && (
        <div className="rounded-lg border border-amber-400/50 bg-amber-50 p-3 text-sm space-y-2 text-amber-900">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{verifyInfo.message}</span>
          </div>
          {verifyInfo.url && (
            <a
              href={verifyInfo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-medium underline"
            >
              Verify my bank account
            </a>
          )}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full h-11"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Processing…
          </span>
        ) : (
          'Pay'
        )}
      </Button>
      <p className="text-xs text-muted-foreground">
        Cards are charged immediately. Bank transfers (ACH) take 3–5
        business days to clear — your entry is confirmed right away on the
        strength of the initiated transfer, with no transaction fee charged.
      </p>
      </form>
    </div>
  );
}

/**
 * Shared Stripe checkout modal used by both single-registration and
 * bulk-pay flows. Deferred-mint variant — no Stripe PI exists until
 * the user clicks Pay.
 *
 * Owns:
 *   - Fetches a deferred-mint quote on open via `quoteOrder` and
 *     re-fetches when `triggerKey` changes.
 *   - Loads Stripe.js with the publishable key from the quote (or
 *     VITE_STRIPE_PUBLISHABLE_KEY fallback).
 *   - Mounts <Elements mode="payment" amount={total} /> with no
 *     client_secret. Supports card and us_bank_account.
 *   - At submit: calls `confirmIntent` to mint the PI, then
 *     stripe.confirmPayment to confirm.
 */
export function StripeCheckoutDialog<TQuote extends DeferredStripeQuote>({
  open,
  onClose,
  title,
  subtitle,
  amountLabel,
  fallbackTotal,
  triggerKey,
  quoteOrder,
  confirmIntent,
  availableCredit = 0,
  creditOptions,
  onSettleWithCredit,
  onApprove,
  onCancel,
  onApproved,
}: StripeCheckoutDialogProps<TQuote>) {
  const { showInfo, showError } = useToast();

  const [quote, setQuote] = useState<TQuote | null>(null);
  // Org credit the user has chosen to apply (split tender). Reset on reopen.
  // `creditApplied` drives the single-org control; `appliedByOrg` drives the
  // multi-org control. Only one is active per dialog (see `isOptions`).
  const [creditApplied, setCreditApplied] = useState(0);
  const [appliedByOrg, setAppliedByOrg] = useState<Record<string, number>>({});
  const [settling, setSettling] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  // Track the user's currently-selected method (card or us_bank_account)
  // so the displayed total flips between card-price and ACH-price.
  // Defaults to card — matches the order in PaymentElement.
  const [selectedMethod, setSelectedMethod] = useState<
    'card' | 'us_bank_account'
  >('card');

  const quoteRef = useRef(quoteOrder);
  quoteRef.current = quoteOrder;

  useEffect(() => {
    if (!open || !triggerKey) return;
    let cancelled = false;
    setQuote(null);
    setQuoteError(null);
    setLoadingQuote(true);
    setSelectedMethod('card');
    setCreditApplied(0);
    setAppliedByOrg({});
    quoteRef
      .current()
      .then((res) => {
        if (cancelled) return;
        setQuote(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setQuoteError(paymentErrorCopy(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingQuote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, triggerKey]);

  const handleClose = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      showInfo('Payment cancelled', 'No charges were made.');
    }
    onClose();
  }, [onCancel, onClose, showInfo]);

  // ── Split tender. Cap the applicable credit to the LOWER method total
  //    (ACH) minus a chargeable residual, so whichever method the user picks
  //    still has ≥ $0.50 going to Stripe (matches the server-side cap, keeping
  //    the Elements amount == minted PI amount). Reduce both method totals by
  //    the applied amount so every downstream amount (display, Elements sync,
  //    wallet) uses the post-credit charge.
  const MIN_STRIPE_CHARGE = 0.5;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const rawCardTotal = quote?.totals_by_method?.card.total_amount;
  const rawAchTotal = quote?.totals_by_method?.us_bank_account.total_amount;
  const lowestRawTotal =
    typeof rawAchTotal === 'number'
      ? rawAchTotal
      : typeof rawCardTotal === 'number'
        ? rawCardTotal
        : quote?.total_amount ?? fallbackTotal ?? 0;

  // ── Credit mode. Multi-org (bulk) baskets pass `creditOptions` (per-org
  //    affordances) and may cover the WHOLE basket (zero-charge). Single-org
  //    (PayNow / manual) flows pass a plain `availableCredit` number and always
  //    leave a chargeable ≥ $0.50 residual. `isOptions` gates ALL new behavior
  //    so the legacy flows are untouched.
  const isOptions = creditOptions !== undefined;
  const options = creditOptions ?? [];
  const multiOrg = options.length > 1;
  const allowZeroCharge = isOptions && !!onSettleWithCredit;

  // The single inline control's cap + full balance (legacy availableCredit, or
  // the lone org option in single-org bulk mode).
  const singleApplicable = isOptions
    ? options[0]?.applicable_credit ?? 0
    : availableCredit;
  const singleTotalBalance = isOptions
    ? options[0]?.total_credit ?? singleApplicable
    : availableCredit;
  // Allow the single control to cover the full basket when zero-charge is
  // permitted; otherwise leave a ≥ $0.50 residual for Stripe.
  const singleCeil = allowZeroCharge
    ? lowestRawTotal
    : Math.max(0, lowestRawTotal - MIN_STRIPE_CHARGE);
  const maxApplicableCredit = Math.max(
    0,
    round2(Math.min(singleApplicable, singleCeil)),
  );

  // When the user's available credit covers the fee-waived balance in full,
  // the card-only transaction fee is moot: applying credit settles the whole
  // balance for $0 and any excess credit stays on the account. Surface that so
  // the cap (which equals the fee-EXCLUDED balance — the most credit can pay)
  // doesn't read as "you're short the fee and must pay it on a card." The fee
  // shown is the CARD fee specifically — it's the amount that's waived once no
  // card is charged.
  const cardFeeTotal =
    quote?.totals_by_method?.card.transaction_fee_total ??
    quote?.transaction_fee_total ??
    0;
  const creditCoversInFull =
    allowZeroCharge &&
    lowestRawTotal > 0 &&
    singleApplicable + 0.005 >= lowestRawTotal;
  const leftoverCredit = round2(
    Math.max(0, singleTotalBalance - lowestRawTotal),
  );

  const sumByOrg = round2(
    Object.values(appliedByOrg).reduce((s, n) => s + (Number(n) || 0), 0),
  );
  const effectiveCredit = multiOrg
    ? Math.max(0, Math.min(sumByOrg, lowestRawTotal))
    : Math.max(0, Math.min(creditApplied, maxApplicableCredit));

  // Per-org applications sent to the backend (multi-org), or the single-org
  // pair, or nothing.
  const creditApplications: CreditApplicationInput[] = multiOrg
    ? Object.entries(appliedByOrg)
        .filter(([, amt]) => (Number(amt) || 0) > 0)
        .map(([organization_id, amount]) => ({
          organization_id,
          amount: Number(amount),
        }))
    : isOptions && options[0] && effectiveCredit > 0
      ? [{ organization_id: options[0].organization_id, amount: effectiveCredit }]
      : [];

  const applyCredit = (n: number | undefined) =>
    typeof n === 'number'
      ? Math.max(MIN_STRIPE_CHARGE, Math.round((n - effectiveCredit) * 100) / 100)
      : n;

  // Residual actually charged for the selected method — NOT floored — used to
  // route between the Stripe form, the zero-charge button, and the disabled
  // dead zone (a sub-$0.50 positive residual Stripe would reject).
  const rawSelectedTotal =
    selectedMethod === 'us_bank_account'
      ? rawAchTotal ?? rawCardTotal
      : rawCardTotal ?? rawAchTotal;
  const baseSelectedTotal =
    typeof rawSelectedTotal === 'number'
      ? rawSelectedTotal
      : quote?.total_amount ?? fallbackTotal ?? 0;
  const residualCharge = round2(baseSelectedTotal - effectiveCredit);
  // Full coverage is judged against the fee-waived (lowest) total, which is
  // what credit-only settlement collects.
  const fullyCovered =
    allowZeroCharge &&
    effectiveCredit > 0 &&
    round2(lowestRawTotal - effectiveCredit) <= 0.005;
  const creditDeadZone =
    isOptions &&
    !fullyCovered &&
    residualCharge > 0.005 &&
    residualCharge < MIN_STRIPE_CHARGE;

  // Resolve the displayed total based on the currently-selected method.
  // When totals_by_method is missing (legacy quote shapes), fall back to
  // the quote's top-level total_amount or the parent-supplied fallback.
  const cardTotal = applyCredit(rawCardTotal);
  const achTotal = applyCredit(rawAchTotal);
  // Credit-reduced per-method totals handed to the Elements form so its
  // amount sync mints/charges the post-credit amount.
  const adjustedTotalsByMethod = quote?.totals_by_method
    ? {
        card: {
          ...quote.totals_by_method.card,
          total_amount: cardTotal ?? quote.totals_by_method.card.total_amount,
        },
        us_bank_account: {
          ...quote.totals_by_method.us_bank_account,
          total_amount:
            achTotal ?? quote.totals_by_method.us_bank_account.total_amount,
        },
      }
    : undefined;
  // Inject the chosen credit into confirmIntent so mint reduces the PI. The
  // inner form keeps calling confirmIntent(paymentId, method) — unchanged.
  const confirmIntentWithCredit = useCallback(
    (paymentId: string, method: 'card' | 'us_bank_account') =>
      confirmIntent(
        paymentId,
        method,
        effectiveCredit > 0 ? effectiveCredit : undefined,
        creditApplications.length > 0 ? creditApplications : undefined,
      ),
    // creditApplications is derived from effectiveCredit + appliedByOrg, both
    // tracked here; serialize to keep the dep array value-stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmIntent, effectiveCredit, JSON.stringify(creditApplications)],
  );

  // Zero-charge path: settle the whole basket with credit, no Stripe.
  const handleSettleWithCredit = useCallback(async () => {
    if (!onSettleWithCredit || creditApplications.length === 0) return;
    setSettling(true);
    try {
      await onSettleWithCredit(creditApplications);
      onClose();
    } catch (err) {
      showError(
        paymentErrorCopy(err, 'Could not settle with credit'),
      );
    } finally {
      setSettling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSettleWithCredit, JSON.stringify(creditApplications), onClose, showError]);
  const displayedTotal =
    selectedMethod === 'us_bank_account' && typeof achTotal === 'number'
      ? achTotal
      : (typeof cardTotal === 'number'
          ? cardTotal
          : quote?.total_amount ?? fallbackTotal ?? null);
  const displayedFeeTotal =
    selectedMethod === 'us_bank_account'
      ? quote?.totals_by_method?.us_bank_account.transaction_fee_total ?? 0
      : quote?.totals_by_method?.card.transaction_fee_total ?? quote?.transaction_fee_total;

  const fallbackPubKey = (import.meta as any).env.VITE_STRIPE_PUBLISHABLE_KEY || '';
  const publishableKey = quote?.stripe_publishable_key || fallbackPubKey;

  // Only load Stripe.js once the dialog is actually open. Both PayNowDialog
  // and BulkPayDialog are mounted (open=false) on every event page; without
  // this gate each one eagerly calls loadStripe and spins up Stripe's
  // controller iframes on page load, adding memory pressure that can crash
  // memory-constrained mobile Safari ("A problem repeatedly occurred").
  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!open || !publishableKey) return null;
    return loadStripe(publishableKey);
  }, [open, publishableKey]);

  // Elements options in deferred mode. The `amount` is what Stripe uses
  // to render method-specific UI (e.g. enabling Klarna above a threshold);
  // the actual capture amount comes from the server-minted PI at submit
  // time. We pin to the CARD price so the form renders consistently —
  // ACH simply waives the fee at mint-time without changing the form.
  const elementsOptions: StripeElementsOptions | null = useMemo(() => {
    if (!quote) return null;
    const amountForElements =
      typeof cardTotal === 'number'
        ? cardTotal
        : quote.total_amount ?? fallbackTotal ?? 0;
    if (amountForElements <= 0) return null;
    return {
      mode: 'payment' as const,
      amount: Math.round(amountForElements * 100),
      currency: 'usd',
      paymentMethodTypes: ['card', 'us_bank_account'],
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#dc2626',
        },
      },
    };
  }, [quote, cardTotal, fallbackTotal]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        hideClose
        className="sm:max-w-md max-h-[90vh] overflow-y-auto p-4 gap-3"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={handleClose}
          className="absolute right-3 top-3 h-7 px-3 text-xs"
        >
          Close
        </Button>

        <DialogHeader>
          <DialogTitle className="pr-20">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {subtitle && (
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          )}

          <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                {amountLabel}
              </span>
              <span className="font-semibold">
                {fullyCovered
                  ? formatCurrency(0, 2)
                  : displayedTotal != null
                    ? formatCurrency(displayedTotal, 2)
                    : '—'}
              </span>
            </div>
            {displayedFeeTotal != null && displayedFeeTotal > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Includes {formatCurrency(displayedFeeTotal, 2)} in
                non-refundable transaction fees.
              </p>
            )}
            {selectedMethod === 'us_bank_account' && (
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                Bank transfer selected — transaction fee waived.
              </p>
            )}

            {/* Split tender — apply org account credit. Multi-org baskets get
                the dropdown + badges control; single-org / legacy get the
                inline input. Org-scoped + capped server-side. */}
            {multiOrg ? (
              <CreditApplyControl
                options={options}
                appliedByOrg={appliedByOrg}
                onChange={setAppliedByOrg}
              />
            ) : (isOptions ? singleApplicable > 0 : availableCredit > 0) ? (
              <div className="mt-3 pt-3 border-t space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    Apply account credit
                    {creditCoversInFull && cardFeeTotal > 0.005 ? (
                      <span className="block text-[11px] text-muted-foreground/80">
                        Your {formatCurrency(lowestRawTotal, 2)} credit covers
                        this balance in full. The{' '}
                        {formatCurrency(cardFeeTotal, 2)} fee applies only if you
                        pay by card — applying credit settles it for{' '}
                        {formatCurrency(0, 2)}
                        {leftoverCredit > 0.005
                          ? `, and ${formatCurrency(
                              leftoverCredit,
                              2,
                            )} credit stays on your account.`
                          : '.'}
                      </span>
                    ) : isOptions &&
                      singleTotalBalance > singleApplicable + 0.005 ? (
                      <span className="block text-[11px] text-muted-foreground/80">
                        Total credit {formatCurrency(singleTotalBalance, 2)} ·{' '}
                        {formatCurrency(singleApplicable, 2)} available to use
                      </span>
                    ) : (
                      <span className="block text-[11px] text-muted-foreground/80">
                        {formatCurrency(singleApplicable, 2)} available
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={maxApplicableCredit}
                      value={creditApplied || ''}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setCreditApplied(
                          isNaN(v) || v < 0
                            ? 0
                            : Math.min(v, maxApplicableCredit),
                        );
                      }}
                      className="w-24 h-8 rounded-md border px-2 text-sm text-right bg-background"
                      placeholder="0.00"
                      aria-label="Credit amount to apply"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => setCreditApplied(maxApplicableCredit)}
                    >
                      Max
                    </Button>
                  </div>
                </div>
                {effectiveCredit > 0 && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    −{formatCurrency(effectiveCredit, 2)} credit applied ·{' '}
                    {fullyCovered
                      ? formatCurrency(0, 2)
                      : displayedTotal != null
                        ? formatCurrency(displayedTotal, 2)
                        : '—'}{' '}
                    {fullyCovered
                      ? 'due'
                      : `via ${selectedMethod === 'us_bank_account' ? 'bank' : 'card'}`}
                  </p>
                )}
                {!allowZeroCharge && maxApplicableCredit < singleApplicable && (
                  <p className="text-[11px] text-muted-foreground">
                    At least {formatCurrency(MIN_STRIPE_CHARGE, 2)} must be paid
                    by card/bank, so up to{' '}
                    {formatCurrency(maxApplicableCredit, 2)} can apply here.
                  </p>
                )}
              </div>
            ) : null}

            {/* Multi-org credit summary — running total applied + resulting due. */}
            {multiOrg && effectiveCredit > 0 && (
              <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">
                −{formatCurrency(effectiveCredit, 2)} credit applied ·{' '}
                {fullyCovered
                  ? `${formatCurrency(0, 2)} due`
                  : `${
                      displayedTotal != null
                        ? formatCurrency(displayedTotal, 2)
                        : '—'
                    } via ${selectedMethod === 'us_bank_account' ? 'bank' : 'card'}`}
              </p>
            )}
          </div>

          {quoteError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm flex gap-2 text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{quoteError}</span>
            </div>
          )}

          {loadingQuote && !quoteError && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing checkout…
            </div>
          )}

          {/* Zero-charge: credit covers the whole basket — settle with no card. */}
          {!loadingQuote && !quoteError && quote?.payment_id && fullyCovered && (
            <div className="space-y-2">
              <Button
                type="button"
                onClick={handleSettleWithCredit}
                disabled={settling}
                className="w-full h-11"
              >
                {settling ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Settling…
                  </span>
                ) : (
                  `Pay ${formatCurrency(effectiveCredit, 2)} with credit`
                )}
              </Button>
              <p className="text-xs text-muted-foreground">
                Your account credit covers this balance in full — no card or
                bank charge.
              </p>
            </div>
          )}

          {/* Dead zone: a sub-$0.50 residual Stripe can't charge. */}
          {!loadingQuote &&
            !quoteError &&
            quote?.payment_id &&
            creditDeadZone && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  This leaves {formatCurrency(residualCharge, 2)} to charge,
                  below the {formatCurrency(MIN_STRIPE_CHARGE, 2)} card minimum.
                  Apply a little more credit to cover the full balance, or
                  reduce it so at least {formatCurrency(MIN_STRIPE_CHARGE, 2)} is
                  charged.
                </span>
              </div>
            )}

          {!loadingQuote &&
            !quoteError &&
            !fullyCovered &&
            !creditDeadZone &&
            quote?.payment_id &&
            stripePromise &&
            elementsOptions && (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <StripeCheckoutForm
                  paymentId={quote.payment_id}
                  confirmIntent={confirmIntentWithCredit}
                  onApprove={onApprove}
                  onApproved={onApproved}
                  onClose={onClose}
                  totalsByMethod={adjustedTotalsByMethod ?? quote.totals_by_method}
                  onMethodChange={(m) =>
                    setSelectedMethod(m === 'us_bank_account' ? 'us_bank_account' : 'card')
                  }
                />
              </Elements>
            )}

          {!loadingQuote && !quoteError && !publishableKey && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
              Stripe is not configured for this environment (missing
              VITE_STRIPE_PUBLISHABLE_KEY and the server didn't return one).
              Please contact the event organizer.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Backwards-compat aliases for callers still mid-cutover. Remove once the
// `Stripe…` names are universal.
export const PayPalCheckoutDialog = StripeCheckoutDialog;
/** @deprecated use DeferredStripeQuote */
export type PayPalOrderLike = DeferredStripeQuote;
/** @deprecated use DeferredStripeQuote */
export type StripeOrderLike = DeferredStripeQuote;
